import "react-markdown-editor-lite/lib/index.css";

import MarkdownIt from "markdown-it";
import emoji from "markdown-it-emoji";
import React, { useEffect, useRef, useState } from "react";
import "./DiscordEditor.css";

import {
  getDiscordChannels,
  getDiscordInitialMembers,
} from "../../lib/discordQueries";
import QuillEditor from "./QuillEditor";
import InsertEditorVariables from "./InsertEditorVariables";
import { PersonalizationValue } from "@dots-projects/types";
import ReactQuill, { Range } from "react-quill";

const mdParser = new MarkdownIt();
mdParser.use(emoji);

type onChangeProps = {
  [key: string]: string;
  html: string;
  text: string;
};

type Props = {
  onChange: (obj: onChangeProps) => void; // The handler for the onChange event
  value: any; // A message state object from parent.
  disablePersonalTokens: boolean; // Disable personalization tokens (e.g. {{first_name}})
  templateText?: string; // the template text to be inserted
  personalizationValues?: PersonalizationValue[]; // Check PersonalizationValue.ts definition
  readOnly?: boolean; // If true, the editor will be read only
};

/**
 * Component that acts as a wrapper for QuillEditor. Fetches channels and members
 * @param props @see Props
 * @returns
 */
function DiscordTextEditor(props: Props) {
  type Members = {
    text: string;
    value: string;
    id: string;
    avatar: string;
  };
  type Channels = {
    text: string;
    value: string;
    id: string;
  };
  const guildId: string = localStorage.getItem("guild-id") as string;
  const [filterList, setFilterList] = useState<Members[]>([]);
  const [channelsList, setChannelsList] = useState<Channels[]>([]);
  const quillEditorRef = useRef<(ReactQuill & { cursorPos: Range }) | null>(
    null
  );

  function handleEditorChange({ html, text }: { html: string; text: string }) {
    props.onChange({
      ...(typeof props.value === "object" ? props.value : {}),
      html,
      text,
    });
  }

  /**
   * Load all users for the given team, parsed into {id: string, value: string} format.
   */
  const loadMembersList = async () => {
    const data = await getDiscordInitialMembers(guildId);
    const membersNameArray: Members[] = [];
    data.initialMembers.forEach((item: any) => {
      const obj: Members = {
        text: item.username,
        value: item.username,
        id: item.discord_id,
        avatar: item.avatar,
      };
      membersNameArray.push(obj);
    });
    setFilterList(membersNameArray);
  };

  /**
   * Load all channels for the given team, parsed into {id: string, value: string} format.
   */
  const loadChannelsList = async () => {
    const data = await getDiscordChannels(guildId);
    const channelsNameArray: Channels[] = [];
    if (data) {
      data.forEach((item: any) => {
        const obj: Channels = {
          text: item.name,
          value: item.name,
          id: item.discord_id,
        };
        channelsNameArray.push(obj);
      });
    }
    setChannelsList(channelsNameArray);
  };

  useEffect(() => {
    loadMembersList();
    loadChannelsList();
  }, []);

  return (
    <div className="dots-discord-editor" style={{ width: "100%" }}>
      <QuillEditor
        readOnly={props.readOnly}
        members={filterList}
        channels={channelsList}
        onChange={handleEditorChange}
        value={(() => {
          return props.value?.html || "";
        })()}
        disablePersonalTokens={props.disablePersonalTokens}
        templateText={props.templateText}
        platform="discord"
        refProp={quillEditorRef}
        personalizationValues={props.personalizationValues}
      >
        {props.personalizationValues && (
          <InsertEditorVariables
            readOnly={props.readOnly}
            insertVariables={props.personalizationValues}
            quillEditorRef={quillEditorRef}
          />
        )}
      </QuillEditor>
    </div>
  );
}

export { DiscordTextEditor };
